import type { CampaignSelectDto } from '@@types/api.types';
import {
  COALITION_PARTNER_LABELS,
  KILL_CHAIN_CATEGORY_LABELS,
  LEAD_COMMAND_LABELS,
} from '@components/pages/campaigns/components/campaign-modal.shared';
import { BadgeList } from '@components/shared/badge-list.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { CampaignStatusBadge } from '@components/shared/campaign-status-badge.component';
import { ClassificationBadge } from '@components/shared/classification-badge.component';
import { Tile } from '@components/shared/tile.component';
import { EditIcon } from 'lucide-react';
import type { FC } from 'react';

type CampaignDetailsTileProps = {
  campaign: CampaignSelectDto;
  onEdit: () => void;
};

export const CampaignDetailsTile: FC<CampaignDetailsTileProps> = (props) => {
  const { campaign, onEdit } = props;
  return (
    <Tile
      header={{
        iconButton: { ariaLabel: 'Edit campaign details', icon: <EditIcon />, onClick: onEdit },
        title: (
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-medium text-gray-800">{campaign.name}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <ClassificationBadge classification={campaign.classificationLevel} />
              <CampaignStatusBadge status={campaign.status} />
            </div>
          </div>
        ),
      }}
      hoverReveal
    >
      {campaign.description && <p className="whitespace-pre-wrap text-sm text-gray-700">{campaign.description}</p>}
      <div className="rounded-lg bg-gray-100 p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ReadOnlyField label="Mission Type Tag">
            {campaign.killChainCategory ? KILL_CHAIN_CATEGORY_LABELS[campaign.killChainCategory] : '—'}
          </ReadOnlyField>
          <ReadOnlyField label="Time Window">{campaign.timeWindow || '—'}</ReadOnlyField>
          <ReadOnlyField label="Owner">{campaign.ownerName || '—'}</ReadOnlyField>
          <ReadOnlyField label="Updated">{new Date(campaign.updatedAt).toLocaleDateString()}</ReadOnlyField>
          <ReadOnlyField label="Lead Commands">
            <CampaignBadgeList values={campaign.leadCommands.map((value) => LEAD_COMMAND_LABELS[value])} />
          </ReadOnlyField>
          <ReadOnlyField label="Coalition Partners">
            <CampaignBadgeList values={campaign.coalitionPartners.map((value) => COALITION_PARTNER_LABELS[value])} />
          </ReadOnlyField>
        </div>
      </div>
    </Tile>
  );
};

type ReadOnlyFieldProps = {
  children: React.ReactNode;
  className?: string;
  label: string;
};

const ReadOnlyField: FC<ReadOnlyFieldProps> = (props) => {
  const { children, className, label } = props;
  return (
    <div className={className}>
      <div className="text-sm font-medium text-gray-600">{children}</div>
      <BlueLabel
        className="mt-0.5"
        text={label}
      />
    </div>
  );
};

type CampaignBadgeListProps = {
  values: string[];
};

const CampaignBadgeList: FC<CampaignBadgeListProps> = (props) => {
  const { values } = props;
  if (values.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <BadgeList
      badgeVariant="outline"
      items={values.map((value) => ({ id: value, name: value }))}
      limit={2}
      size="xs"
    />
  );
};
